/**
 * Route configuration constants
 */

export const ROUTES = {
  // Public routes
  HOME: '/',

  // Unified home (Send/Deliver tabs)
  DASHBOARD: '/home',

  // Auth routes
  AUTH: {
    ROOT: '/auth',
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    JETT_REGISTER: '/auth/jett-register',
    BUSINESS_REGISTER: '/auth/business-register',
    RECOVERY_PASSWORD: '/auth/recovery-password',
    WELCOME: '/auth/welcome',
    ACCOUNT_TYPE: '/auth/account-type',
    INTENT_SELECTION: '/auth/intent-selection',
    KYC_EDUCATION: '/auth/kyc-education',
    PROCESS_BIOMETRIC: '/auth/process-biometric',
    VALUE_PROPOSITION: '/auth/value-proposition',
    B2B_INVITE: (token: string) => `/auth/b2b-invite/${token}`,
  },

  // Sender routes (legacy - redirect to unified home)
  SENDER: {
    ROOT: '/sender',
    DASHBOARD: '/home', // Now points to unified home
    PACKAGES: '/sender/packages',
    PACKAGE_NEW: '/sender/packages/new',
    PACKAGE_DETAIL: (id: string) => `/sender/packages/${id}`,
    CHAT: '/sender/chat',
    CHAT_DETAIL: (id: string) => `/sender/chat/${id}`,
    PROFILE: '/sender/profile',
    SETTINGS: '/sender/settings',
    PAYMENT_METHODS: '/sender/payment-methods',
    INCENTIVES: '/sender/incentives',
    REFERRALS: '/sender/referrals',
    NEARBY_MAP: '/sender/nearby-map',
  },

  // Jetpacker routes (legacy - redirect to unified home)
  JETPACKER: {
    ROOT: '/jetpacker',
    DASHBOARD: '/home', // Now points to unified home
    FULL_DASHBOARD: '/jetpacker/dashboard', // Full jetpacker dashboard
    ITINERARIES: '/jetpacker/itineraries',
    ITINERARY_NEW: '/jetpacker/itineraries/new',
    ITINERARY_CONFIRMATION: '/jetpacker/itineraries/confirmation',
    ITINERARY_DETAIL: (id: string) => `/jetpacker/itineraries/${id}`,
    ITINERARY_EDIT: (id: string) => `/jetpacker/itineraries/${id}/edit`,
    DELIVERIES: '/jetpacker/deliveries',
    DELIVERY_PROCESS: (id: string) => `/jetpacker/deliveries/${id}`,
    COLLECTION_PROCESS: (id: string) => `/jetpacker/collection/${id}`,
    REQUESTS: '/jetpacker/requests', // Pending delivery requests
    ROUTES: '/jetpacker/routes',
    CHAT: '/jetpacker/chat',
    CHAT_DETAIL: (id: string) => `/jetpacker/chat/${id}`,
    PROFILE: '/jetpacker/profile',
    BANK_ACCOUNT: '/jetpacker/bank-account',
    EARNINGS: '/jetpacker/earnings',
    WITHDRAW: '/jetpacker/withdraw',
    BADGES: '/jetpacker/badges',
    LEVELS: '/jetpacker/levels',
    PERFORMANCE: '/jetpacker/performance',
    INCENTIVES: '/jetpacker/incentives',
    REFERRALS: '/jetpacker/referrals',
    W9_FORM: '/jetpacker/w9-form',
    CHAT_AI: '/jetpacker/chat-ai',
    PERKS: '/jetpacker/perks',
  },

  // Products routes (Sender's product/package management)
  PRODUCTS: {
    ROOT: '/products',
    LIST: '/products',
    NEW: '/products/new',
    CHECKOUT_SUCCESS: '/products/checkout-success',
    DETAIL: (id: string) => `/products/${id}`,
  },

  // Tracking routes (Receiver's package tracking view)
  TRACKING: {
    ROOT: '/tracking',
    DETAIL: (id: string) => `/tracking/${id}`,
    PUBLIC_ROOT: '/track',
    PUBLIC_DETAIL: (code: string) => `/track/${code}`,
  },

  // Claims/disputes routes
  CLAIMS: {
    ROOT: '/claims',
    LIST: '/claims',
    NEW: '/claims/new',
    DETAIL: (id: string) => `/claims/${id}`,
  },

  // B2B operator routes
  B2B: {
    ROOT: '/b2b',
    DASHBOARD: '/b2b/dashboard',
    PRICING: '/b2b/pricing',
    PACKAGES: '/b2b/packages',
    PACKAGE_DETAIL: (id: string) => `/b2b/packages/${id}`,
    BULK_UPLOAD: '/b2b/bulk-upload',
    REPORTS: '/b2b/reports',
    COLLECTORS: '/b2b/collectors',
    TEAM: '/b2b/team',
    PROFILE: '/b2b/profile',
    COLLECTOR: '/b2b/collector',
    PAYMENT_METHODS: '/b2b/payment-methods',
    ACCOUNT_STATEMENT: '/b2b/account-statement',
    RECEIPT: (id: string) => `/b2b/receipt/${id}`,
    TE_LO_LLEVO: '/b2b/te-lo-llevo',
  },

  // Shared routes
  ABOUT: '/about',
  EMPRESAS: '/empresas',
  PUBLIC_SHIPMENT: '/quote_te-lo-llevo',
  NOTIFICATIONS: '/notifications',
  FAQ: '/faq',
  REFUND_POLICY: '/refund-policy',
  PRICING_TABLE: '/pricing-table',
  DOCUMENTS: (name: string) => `/documents/${name}`,
  TERMS: (name: string) => `/terms-conditions/${name}`,
  NOTIFICATIONS_SETTINGS: '/notifications-settings',
  PRIVACY_SECURITY: '/privacy-security',
  FEEDBACK: '/feedback',

  // Te Lo Llevo (shared between sender and jetpacker)
  TE_LO_LLEVO: '/te-lo-llevo',

  // Rating redirect (from email)
  RATE_JETSENDER: '/rate-jetsender',

  // Payment return routes
  STRIPE_RETURN: '/stripe-return',
  CONNECT_RETURN: '/connect-return',
  CONNECT_REFRESH: '/connect/refresh',
} as const;

// Route groups for guards
export const PUBLIC_ROUTES = [
  ROUTES.HOME,
  ROUTES.AUTH.ROOT,
  ROUTES.AUTH.LOGIN,
  ROUTES.AUTH.REGISTER,
  ROUTES.AUTH.JETT_REGISTER,
  ROUTES.AUTH.BUSINESS_REGISTER,
  ROUTES.AUTH.RECOVERY_PASSWORD,
  ROUTES.AUTH.WELCOME,
  ROUTES.TRACKING.PUBLIC_ROOT,
];

export const AUTH_ROUTES = Object.values(ROUTES.AUTH);

export const SENDER_ROUTES = Object.values(ROUTES.SENDER).filter(
  (route) => typeof route === 'string'
);

export const JETPACKER_ROUTES = Object.values(ROUTES.JETPACKER).filter(
  (route) => typeof route === 'string'
);
